import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, InputAdornment, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { buildApiUrl } from "../../apiConfig";
import OnlinePriceReductionPanel from "./OnlinePriceReductionPanel";
import { getExpirationInfo, normalizePriceSearch, priceReductionErrorMessage } from "./priceReduction";

export default function OnlinePriceReductionTab() {
  const [query, setQuery] = useState("");
  const [state, setState] = useState({});
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const requestRef = useRef(0);
  const controllerRef = useRef(null);
  const resultButtonRefs = useRef(new Map());

  const closePanel = () => {
    resultButtonRefs.current.get(selectedIssueId)?.focus();
    setSelectedIssueId(null);
  };

  useEffect(() => () => {
    requestRef.current += 1;
    controllerRef.current?.abort();
  }, []);

  const handleQueryChange = (value) => {
    requestRef.current += 1;
    controllerRef.current?.abort();
    setQuery(value);
    setState({});
    setSelectedIssueId(null);
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const search = normalizePriceSearch(query);
    if (search.length < 2) return;
    controllerRef.current?.abort();
    const requestId = ++requestRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    setSelectedIssueId(null);
    setState({ loading: true, search });
    try {
      const { data } = await axios.get(buildApiUrl("/api/tasks/online-price-reduction/search"), {
        params: { q: search },
        signal: controller.signal,
      });
      if (requestId !== requestRef.current) return;
      if (data?.ok !== true || !Array.isArray(data.tasks)) {
        throw new Error("Rezultatele căutării nu au putut fi verificate.");
      }
      setState({ loading: false, search, tasks: data.tasks, hasMore: data.hasMore });
    } catch (error) {
      if (requestId !== requestRef.current) return;
      setState({ loading: false, search, error: priceReductionErrorMessage(error, "Căutarea nu a putut fi finalizată. Reîncearcă.") });
    }
  };

  return (
    <Box sx={{
      width: "100%", minWidth: 0, display: "grid", gap: 2, alignItems: "start",
      gridTemplateColumns: selectedIssueId
        ? { xs: "minmax(0, 1fr)", xl: "minmax(0, 1fr) minmax(420px, min(32vw, 540px))" }
        : "minmax(0, 1fr)",
    }}>
      <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
      <Paper variant="outlined" sx={{ maxWidth: 740, mx: "auto", p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} textAlign="center">Diminuare Preț</Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 0.75, mb: 2.5 }}>
          Caută un produs Online după numărul contractului sau SKU.
        </Typography>
        <Stack component="form" onSubmit={handleSearch} direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            label="Număr contract sau SKU"
            placeholder="Ex. CN1663 sau SKU produs"
            value={query}
            onChange={(event) => handleQueryChange(event.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: 80 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <Button type="submit" variant="contained" disabled={normalizePriceSearch(query).length < 2 || state.loading}
            sx={{ minWidth: 120, textTransform: "none" }}>
            {state.loading ? "Se caută…" : "Caută"}
          </Button>
        </Stack>
      </Paper>
      {state.loading && (
        <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
          <CircularProgress aria-label="Se caută produse Online" />
          <Typography color="text.secondary">Se caută produsele Online…</Typography>
        </Stack>
      )}
      {state.error && <Alert severity="error">{state.error}</Alert>}
      {state.tasks && state.tasks.length === 0 && (
        <Alert severity="info">Nu am găsit produse Online pentru „{state.search}”. Verifică numărul contractului sau SKU-ul.</Alert>
      )}
      {state.tasks?.length > 0 && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {state.tasks.length} {state.tasks.length === 1 ? "produs găsit" : "produse găsite"}. Selectează produsul pentru diminuarea prețului.
          </Typography>
          {state.hasMore && <Alert severity="info" sx={{ mb: 1.5 }}>Există și alte rezultate. Completează numărul contractului sau SKU-ul pentru o căutare mai precisă.</Alert>}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small" aria-label="Produse Online pentru diminuare preț" sx={{ minWidth: 740, "& td, & th": { px: 1.5, py: 1.5 } }}>
              <TableHead><TableRow>
                <TableCell>Issue ID</TableCell><TableCell>Produs</TableCell><TableCell>Status</TableCell>
                <TableCell>Data</TableCell><TableCell>Expiră</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {state.tasks.map((task) => {
                  const expiration = getExpirationInfo(task.customfield_10078);
                  return (
                  <TableRow key={task.issue_id} hover selected={selectedIssueId === task.issue_id}
                    onClick={() => setSelectedIssueId(task.issue_id)}
                    sx={{ cursor: "pointer", "&:hover": { boxShadow: 2 } }}>
                    <TableCell>
                      <Button
                        ref={(element) => {
                          if (element) resultButtonRefs.current.set(task.issue_id, element);
                          else resultButtonRefs.current.delete(task.issue_id);
                        }}
                        aria-label={`Deschide diminuarea prețului pentru ${task.issue_id}`}
                        aria-expanded={selectedIssueId === task.issue_id}
                        aria-controls={selectedIssueId === task.issue_id ? "online-price-panel" : undefined}
                        sx={{ minWidth: 0, p: 0.5, color: "inherit", textTransform: "none" }}
                      >{task.issue_id}</Button>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360, overflowWrap: "anywhere" }}>
                      <Typography variant="body2">{task.summary || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">Contract: {task.nrContract || "—"} · SKU: {task.sku || "—"}</Typography>
                    </TableCell>
                    <TableCell>{task.status || "ONLINE"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{expiration.date}</TableCell>
                    <TableCell><Chip size="small" label={expiration.label} color={expiration.color} sx={{ fontWeight: 700 }} /></TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      </Box>
      {selectedIssueId && (
        <Box sx={{ minWidth: 0, maxWidth: "100%", position: { xl: "sticky" }, top: { xl: 88 }, pr: { xl: 0.5 } }}>
          <OnlinePriceReductionPanel key={selectedIssueId} issueId={selectedIssueId} onClose={closePanel} />
        </Box>
      )}
    </Box>
  );
}
